import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { authslice } from '../store/auth/authSlice'
import NotificationBell from './NotificationBell'
import { C, fontStack } from '../constant/theme'

export default function Navbar({ variant = 'user' }) {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const user = useSelector((state) => state.auth.user)
  const isAdmin = user?.isAdmin || false
  const [menuOpen, setMenuOpen] = useState(false)
  const [mobile, setMobile] = useState(window.innerWidth < 640)
  const menuRef = useRef(null)
  const admin = variant === 'admin'

  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < 640)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  useEffect(() => {
    if (!menuOpen) return
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [menuOpen])

  const logout = () => {
    dispatch(authslice.actions.logout())
    navigate('/login', { replace: true })
  }

  const lnk = { fontSize: 13, fontWeight: 600, textDecoration: 'none', color: admin ? 'rgba(220,232,245,0.8)' : C.navy2 }

  return (
    <>
      {admin && <div style={S.adminStripe} />}
      <header style={admin ? S.headerAdmin : S.headerUser}>
        <a href={admin ? '/admin' : '/'} style={{ ...S.brand, color: admin ? '#fff' : C.text }}>
          🧭 <strong>Tour Timeline</strong>
          {admin && <span style={S.adminBadge}>ADMIN</span>}
        </a>

        {!mobile ? (
          <div style={S.right}>
            {admin ? (
              <>
                <a href="/admin/users" style={lnk}>
                  Users
                </a>
                <a href="/" style={lnk}>
                  Feed ↗
                </a>
              </>
            ) : (
              isAdmin && (
                <a href="/admin" style={lnk}>
                  Dashboard
                </a>
              )
            )}
            <NotificationBell />
            <a href="/profile" style={{ ...lnk, color: admin ? '#fff' : C.text, fontWeight: 700 }}>
              {user?.name}
            </a>
            <button style={admin ? S.signOutDark : S.signOutLight} onClick={logout}>
              Sign out
            </button>
          </div>
        ) : (
          <div style={S.mobileRight} ref={menuRef}>
            <NotificationBell />
            <button style={admin ? S.hamburgerDark : S.hamburgerLight} onClick={() => setMenuOpen((v) => !v)} aria-label={menuOpen ? 'Close menu' : 'Open menu'}>
              {menuOpen ? '✕' : '☰'}
            </button>
            {menuOpen && (
              <div style={S.dropdown}>
                <span style={S.dropName}>{user?.name}</span>
                {admin ? (
                  <>
                    <a href="/admin/users" style={S.dropLink} onClick={() => setMenuOpen(false)}>
                      Users
                    </a>
                    <a href="/" style={S.dropLink} onClick={() => setMenuOpen(false)}>
                      Feed ↗
                    </a>
                  </>
                ) : (
                  isAdmin && (
                    <a href="/admin" style={S.dropLink} onClick={() => setMenuOpen(false)}>
                      Dashboard
                    </a>
                  )
                )}
                <a href="/profile" style={S.dropLink} onClick={() => setMenuOpen(false)}>
                  Profile
                </a>
                <button style={S.dropLogout} onClick={logout}>
                  Sign out
                </button>
              </div>
            )}
          </div>
        )}
      </header>
    </>
  )
}

const S = {
  adminStripe: { height: 4, background: 'linear-gradient(90deg, #d97706 0%, #f59e0b 100%)', flexShrink: 0 },
  headerAdmin: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0 24px',
    height: 56,
    background: C.navy,
    borderBottom: '1px solid rgba(255,255,255,0.07)',
    boxShadow: '0 2px 12px rgba(0,0,0,0.35)',
    fontFamily: fontStack,
    flexShrink: 0,
  },
  headerUser: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0 24px',
    height: 56,
    background: '#fff',
    borderBottom: `1px solid ${C.cardBorder}`,
    fontFamily: fontStack,
    flexShrink: 0,
  },
  brand: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    textDecoration: 'none',
    fontSize: 17,
  },
  adminBadge: {
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: '0.08em',
    background: '#d97706',
    color: '#fff',
    padding: '2px 7px',
    borderRadius: 5,
  },
  right: { display: 'flex', alignItems: 'center', gap: 16 },
  signOutDark: {
    border: '1px solid rgba(255,255,255,0.22)',
    borderRadius: 8,
    padding: '6px 12px',
    fontSize: 12.5,
    fontWeight: 600,
    cursor: 'pointer',
    background: 'transparent',
    color: 'rgba(220,232,245,0.85)',
    fontFamily: 'inherit',
  },
  signOutLight: {
    border: `1.5px solid ${C.inputBorder}`,
    borderRadius: 8,
    padding: '6px 12px',
    fontSize: 12.5,
    fontWeight: 600,
    cursor: 'pointer',
    background: 'transparent',
    color: C.navy2,
    fontFamily: 'inherit',
  },
  mobileRight: { display: 'flex', alignItems: 'center', gap: 8, position: 'relative' },
  hamburgerDark: {
    width: 38,
    height: 38,
    borderRadius: 10,
    border: '1px solid rgba(255,255,255,0.22)',
    background: 'transparent',
    color: 'rgba(220,232,245,0.9)',
    fontSize: 17,
    cursor: 'pointer',
    fontFamily: 'inherit',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  hamburgerLight: {
    width: 38,
    height: 38,
    borderRadius: 10,
    border: `1.5px solid ${C.inputBorder}`,
    background: 'transparent',
    color: C.navy2,
    fontSize: 17,
    cursor: 'pointer',
    fontFamily: 'inherit',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  dropdown: {
    position: 'absolute',
    top: 'calc(100% + 8px)',
    right: 0,
    background: '#fff',
    border: `1px solid ${C.cardBorder}`,
    borderRadius: 12,
    boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
    zIndex: 200,
    minWidth: 170,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  dropName: {
    padding: '12px 16px 10px',
    fontSize: 13,
    fontWeight: 700,
    color: C.text,
    borderBottom: `1px solid ${C.cardBorder}`,
    display: 'block',
  },
  dropLink: {
    padding: '11px 16px',
    fontSize: 13.5,
    fontWeight: 600,
    color: C.navy2,
    textDecoration: 'none',
    borderBottom: `1px solid ${C.cardBorder}`,
    display: 'block',
  },
  dropLogout: {
    padding: '11px 16px',
    fontSize: 13.5,
    fontWeight: 600,
    color: C.danger,
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontFamily: 'inherit',
    textAlign: 'left',
    width: '100%',
  },
}
